import { useState } from 'react';
import type { PushPermission } from '@/mocks/notifications';
import { pushPermissions } from '@/mocks/notifications';

export default function PermissionsPanel() {
  const [perms, setPerms] = useState(pushPermissions);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestedPermId, setRequestedPermId] = useState<string | null>(null);

  const toggle = (id: string) => {
    const perm = perms.find((p) => p.id === id);
    if (!perm) return;

    if (!perm.granted) {
      setRequestedPermId(id);
      setShowRequestDialog(true);
      return;
    }

    // If required, can't disable
    if (perm.required) return;

    setPerms(perms.map((p) => (p.id === id ? { ...p, granted: !p.granted } : p)));
  };

  const confirmRequest = () => {
    if (requestedPermId) {
      setPerms(perms.map((p) => (p.id === requestedPermId ? { ...p, granted: true } : p)));
    }
    setShowRequestDialog(false);
    setRequestedPermId(null);
  };

  const cancelRequest = () => {
    setShowRequestDialog(false);
    setRequestedPermId(null);
  };

  const grantedCount = perms.filter((p) => p.granted).length;

  const requestedPerm = perms.find((p) => p.id === requestedPermId);

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-lg font-bold text-foreground-950">Permisos Push</h2>
        <p className="text-xs text-foreground-500 mt-0.5">Gestiona cómo y dónde recibes las notificaciones</p>
      </div>

      {/* Status Banner */}
      <div className={`rounded-xl p-4 mb-5 flex items-center gap-3 ${
        grantedCount === perms.length
          ? 'bg-emerald-50 border border-emerald-200'
          : grantedCount >= 3
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-red-50 border border-red-200'
      }`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          grantedCount === perms.length
            ? 'bg-emerald-100 text-emerald-600'
            : grantedCount >= 3
              ? 'bg-amber-100 text-amber-600'
              : 'bg-red-100 text-red-600'
        }`}>
          <i className={`${
            grantedCount === perms.length
              ? 'ri-check-double-line'
              : grantedCount >= 3
                ? 'ri-error-warning-line'
                : 'ri-close-circle-line'
          } text-lg`}></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground-900">
            {grantedCount === perms.length
              ? 'Todos los permisos concedidos'
              : grantedCount >= 3
                ? `${grantedCount} de ${perms.length} permisos concedidos`
                : 'Permisos limitados'}
          </p>
          <p className="text-[11px] text-foreground-600 mt-0.5">
            {grantedCount === perms.length
              ? 'Tus notificaciones funcionarán al máximo.'
              : 'Algunas notificaciones pueden no entregarse correctamente.'}
          </p>
        </div>
      </div>

      {/* Permissions List */}
      <div className="space-y-1">
        {perms.map((perm) => (
          <div
            key={perm.id}
            className="bg-background-50 rounded-xl px-4 py-3 flex items-center gap-3 transition-all"
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
              perm.granted ? 'bg-primary-100 text-primary-600' : 'bg-foreground-100 text-foreground-400'
            }`}>
              <i className={`${perm.icon} text-base`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-foreground-900">{perm.label}</h4>
                {perm.required && (
                  <span className="px-1.5 py-0.5 rounded-md bg-primary-100 text-primary-600 text-[9px] font-bold whitespace-nowrap">Obligatorio</span>
                )}
              </div>
              <p className="text-[11px] text-foreground-500 mt-0.5">{perm.description}</p>
            </div>
            <button
              onClick={() => toggle(perm.id)}
              className={`w-10 h-6 rounded-full relative transition-colors shrink-0 cursor-pointer ${
                perm.granted ? 'bg-primary-500' : 'bg-foreground-300'
              } ${perm.required ? 'cursor-not-allowed' : ''}`}
              disabled={perm.required}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                  perm.granted ? 'left-[18px]' : 'left-0.5'
                }`}
              ></div>
            </button>
          </div>
        ))}
      </div>

      {/* Reset */}
      <div className="mt-5 p-4 bg-background-50 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground-900">Restablecer permisos</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Vuelve al estado recomendado de permisos</p>
          </div>
          <button
            onClick={() => setPerms(pushPermissions)}
            className="px-3 py-1.5 text-xs font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-200 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            Restablecer
          </button>
        </div>
      </div>

      {/* Request Dialog Modal */}
      {showRequestDialog && requestedPerm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4 shadow-lg">
            <div className="text-center mb-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${requestedPerm.icon} text-primary-600 text-xl`}></i>
              </div>
              <h3 className="text-base font-bold text-foreground-950">Solicitar permiso</h3>
              <p className="text-sm text-foreground-500 mt-1">
                ¿Deseas activar &ldquo;{requestedPerm.label}&rdquo;?
              </p>
              <p className="text-xs text-foreground-400 mt-1">
                {requestedPerm.description}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={cancelRequest}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRequest}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                Permitir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}